import { createTheme, type ThemeOptions } from '@mui/material/styles'

/**
 * Agribot AI design tokens, mirroring `design/colors_and_type.css` one for one.
 * Component code reads these through `theme.agribot.*` rather than repeating
 * hex values, and every map colour comes from here too.
 */
export const tokens = {
  color: {
    brandDeep: '#295b54',
    brandDeep700: '#1f4741',
    brandDeep900: '#112624',
    brandTeal: '#2a81a0',
    brandTeal100: '#e8f2f6',
    brandTeal300: '#9cc4d2',
    brandGreen: '#60be94',
    brandGreen100: '#e4f4ec',
    brandGreen300: '#aadcc2',
    brandGreen700: '#3d9670',
    ink0: '#ffffff',
    ink50: '#fafaf8',
    ink100: '#f3f3f0',
    ink200: '#e7e7e3',
    ink300: '#cfcfc9',
    ink400: '#9a9a92',
    ink500: '#6e6e66',
    ink600: '#4a4a44',
    ink700: '#2f2f2c',
    ink800: '#1c1c1a',
    ink900: '#0f0f0e',
    dataRisk: { low: '#60be94', med: '#e5b254', high: '#c96a4a' },
    success: '#3d9670',
    warning: '#b88a2f',
    danger: '#b14a32',
    info: '#2a81a0',
    /** Tinted pill backgrounds, as used by the mockups. */
    tintDanger: '#f6e3dc',
    tintWarning: '#f7eedb',
  },
  font: {
    /*
     * Geist has Latin only. The supplier portal also speaks Chinese, Korean
     * and Thai, so each script falls through to the system's own face for it
     * (macOS, Windows, then Noto on Linux and Android). System fonts, not a
     * web font: Google Fonts is out of reach in mainland China.
     */
    sans: "'Geist', system-ui, -apple-system, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Apple SD Gothic Neo', 'Malgun Gothic', 'Thonburi', 'Leelawadee UI', 'Noto Sans', 'Noto Sans SC', 'Noto Sans CJK SC', 'Noto Sans KR', 'Noto Sans CJK KR', 'Noto Sans Thai', sans-serif",
    display: "'Nunito', system-ui, -apple-system, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, Menlo, monospace",
  },
  radius: { xs: 2, sm: 4, md: 6, lg: 10, xl: 14, pill: 999 },
  shadow: {
    1: '0 1px 0 rgba(17,38,36,0.04), 0 1px 2px rgba(17,38,36,0.04)',
    2: '0 2px 4px rgba(17,38,36,0.05), 0 1px 1px rgba(17,38,36,0.03)',
    3: '0 8px 24px -8px rgba(17,38,36,0.12), 0 2px 6px rgba(17,38,36,0.04)',
    float: '0 24px 64px -24px rgba(17,38,36,0.25)',
    ring: '0 0 0 3px rgba(42,129,160,0.22)',
  },
  motion: {
    easeStd: 'cubic-bezier(.2,.6,.2,1)',
    easeOut: 'cubic-bezier(.16,1,.3,1)',
    dur: { fast: 120, base: 200, slow: 360 },
  },
} as const

/** Plot fill/stroke by status — 30–40% alpha fill over a solid stroke. */
export const STATUS_COLOURS = {
  passed: { fill: 'rgba(96,190,148,0.32)', stroke: tokens.color.brandGreen700, tint: tokens.color.brandGreen100, text: tokens.color.success },
  flagged: { fill: 'rgba(229,178,84,0.36)', stroke: tokens.color.warning, tint: tokens.color.tintWarning, text: tokens.color.warning },
  rejected: { fill: 'rgba(201,106,74,0.40)', stroke: tokens.color.danger, tint: tokens.color.tintDanger, text: tokens.color.danger },
  excluded: { fill: 'rgba(154,154,146,0.28)', stroke: tokens.color.ink400, tint: tokens.color.ink100, text: tokens.color.ink500 },
} as const

export type PlotStatus = keyof typeof STATUS_COLOURS

declare module '@mui/material/styles' {
  interface Theme {
    agribot: typeof tokens
  }
  interface ThemeOptions {
    agribot?: typeof tokens
  }
}

const options: ThemeOptions = {
  agribot: tokens,
  palette: {
    mode: 'light',
    primary: { main: tokens.color.brandDeep, dark: tokens.color.brandDeep700, light: '#3d7972', contrastText: '#fff' },
    secondary: { main: tokens.color.brandTeal, dark: '#18576e', light: tokens.color.brandTeal300, contrastText: '#fff' },
    success: { main: tokens.color.brandGreen700, light: tokens.color.brandGreen, contrastText: '#fff' },
    warning: { main: tokens.color.warning, contrastText: '#fff' },
    error: { main: tokens.color.danger, contrastText: '#fff' },
    info: { main: tokens.color.brandTeal, contrastText: '#fff' },
    background: { default: tokens.color.ink50, paper: tokens.color.ink0 },
    text: { primary: tokens.color.ink700, secondary: tokens.color.ink500, disabled: tokens.color.ink400 },
    divider: tokens.color.ink200,
    grey: {
      50: tokens.color.ink50, 100: tokens.color.ink100, 200: tokens.color.ink200,
      300: tokens.color.ink300, 400: tokens.color.ink400, 500: tokens.color.ink500,
      600: tokens.color.ink600, 700: tokens.color.ink700, 800: tokens.color.ink800,
      900: tokens.color.ink900,
    },
  },
  shape: { borderRadius: tokens.radius.md },
  spacing: 4,
  typography: {
    fontFamily: tokens.font.sans,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    // The one display slot per screen: Nunito 700, as the mockups use it.
    h1: { fontFamily: tokens.font.display, fontWeight: 700, fontSize: 30, lineHeight: 1.1, letterSpacing: '-0.01em' },
    h2: { fontFamily: tokens.font.display, fontWeight: 700, fontSize: 24, lineHeight: 1.15, letterSpacing: '-0.01em' },
    h3: { fontFamily: tokens.font.sans, fontWeight: 600, fontSize: 20, lineHeight: 1.25, letterSpacing: '-0.01em' },
    h4: { fontFamily: tokens.font.sans, fontWeight: 600, fontSize: 16, lineHeight: 1.3 },
    h5: { fontFamily: tokens.font.sans, fontWeight: 600, fontSize: 15, lineHeight: 1.3 },
    h6: { fontFamily: tokens.font.sans, fontWeight: 500, fontSize: 14, lineHeight: 1.3 },
    subtitle1: { fontFamily: tokens.font.sans, fontWeight: 500, fontSize: 14, lineHeight: 1.3 },
    subtitle2: { fontFamily: tokens.font.sans, fontWeight: 500, fontSize: 13, lineHeight: 1.3 },
    body1: { fontFamily: tokens.font.sans, fontSize: 15, lineHeight: 1.45 },
    body2: { fontFamily: tokens.font.sans, fontSize: 13, lineHeight: 1.45 },
    caption: { fontFamily: tokens.font.sans, fontSize: 12, lineHeight: 1.4 },
    overline: {
      fontFamily: tokens.font.sans, fontSize: 11, fontWeight: 500,
      letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.2,
    },
    button: { fontFamily: tokens.font.sans, fontWeight: 500, fontSize: 14, letterSpacing: 0, textTransform: 'none' },
  },
  shadows: [
    'none', tokens.shadow[1], tokens.shadow[2], tokens.shadow[3], tokens.shadow.float,
    ...(Array(20).fill(tokens.shadow.float) as string[]),
  ] as never,
  transitions: {
    easing: {
      easeInOut: tokens.motion.easeStd,
      easeOut: tokens.motion.easeOut,
      easeIn: 'cubic-bezier(.4,0,1,1)',
      sharp: tokens.motion.easeStd,
    },
    duration: {
      shortest: tokens.motion.dur.fast, shorter: tokens.motion.dur.fast,
      short: tokens.motion.dur.base, standard: tokens.motion.dur.base,
      complex: tokens.motion.dur.slow, enteringScreen: tokens.motion.dur.base,
      leavingScreen: tokens.motion.dur.fast,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.color.ink50,
          color: tokens.color.ink700,
          WebkitFontSmoothing: 'antialiased',
          textRendering: 'optimizeLegibility',
        },
        // Numbers are tabular everywhere, per the design rules.
        '.tnum': { fontVariantNumeric: 'tabular-nums' },
        '*:focus-visible': { outline: 'none', boxShadow: tokens.shadow.ring },

        // Loading motion, declared once so every skeleton and the fetch bar
        // share one definition rather than each emitting its own keyframes.
        '@keyframes pc-shimmer': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(100%)' },
        },
        '@keyframes pc-sweep': {
          '0%': { transform: 'translateX(-60%) scaleX(0.35)' },
          '50%': { transform: 'translateX(20%) scaleX(0.6)' },
          '100%': { transform: 'translateX(140%) scaleX(0.35)' },
        },
        // A placeholder that pulses is a courtesy; one that cannot be turned
        // off is not. Without motion it is still a shape holding the layout.
        '@media (prefers-reduced-motion: reduce)': {
          '*': { animationDuration: '0.001ms !important', animationIterationCount: '1 !important' },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, variant: 'contained' },
      styleOverrides: {
        root: {
          height: 36, padding: '0 14px', borderRadius: tokens.radius.md,
          textTransform: 'none', fontWeight: 500, boxShadow: 'none', whiteSpace: 'nowrap',
          transition: `background ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
          '&:focus-visible': { boxShadow: tokens.shadow.ring },
        },
        sizeSmall: { height: 32, padding: '0 12px', fontSize: 13 },
        sizeLarge: { height: 44, padding: '0 20px', fontSize: 15 },
        containedPrimary: {
          backgroundColor: tokens.color.brandDeep,
          '&:hover': { backgroundColor: tokens.color.brandDeep700 },
        },
        outlined: {
          borderColor: tokens.color.ink300, color: tokens.color.ink700, backgroundColor: tokens.color.ink0,
          '&:hover': { backgroundColor: tokens.color.ink100, borderColor: tokens.color.ink300 },
        },
        text: { color: tokens.color.ink700, '&:hover': { backgroundColor: tokens.color.ink100 } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.md, color: tokens.color.ink500,
          '&:hover': { backgroundColor: tokens.color.ink100 },
        },
      },
    },
    MuiTextField: { defaultProps: { size: 'small', variant: 'outlined' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.color.ink0, borderRadius: tokens.radius.md, fontSize: 13,
          '& fieldset': { borderColor: tokens.color.ink300 },
          '&:hover fieldset': { borderColor: tokens.color.ink400 },
          '&.Mui-focused': { boxShadow: tokens.shadow.ring },
          '&.Mui-focused fieldset': { borderColor: tokens.color.brandTeal, borderWidth: 1 },
        },
        input: { height: 36, padding: '0 10px', boxSizing: 'border-box' },
        inputSizeSmall: { height: 32, padding: '0 10px' },
      },
    },
    // A select's box is not the input the heights above set (MUI gives it height: auto): match the text fields beside it.
    MuiSelect: {
      styleOverrides: {
        select: {
          display: 'flex', alignItems: 'center', boxSizing: 'border-box', minHeight: 36, paddingTop: 0, paddingBottom: 0,
          '.MuiInputBase-sizeSmall &': { minHeight: 32 },
        },
      },
    },
    MuiMenuItem: { styleOverrides: { root: { fontSize: 13, minHeight: 32 } } },
    MuiInputLabel: { styleOverrides: { root: { fontSize: 13, fontWeight: 500, color: tokens.color.ink700 } } },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none', borderRadius: tokens.radius.md },
        outlined: { borderColor: tokens.color.ink200 },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0, variant: 'outlined' },
      styleOverrides: { root: { borderColor: tokens.color.ink200, borderRadius: tokens.radius.md } },
    },
    MuiCardContent: { styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } } },
    MuiChip: {
      styleOverrides: {
        root: { height: 22, fontSize: 11, fontWeight: 500, borderRadius: tokens.radius.pill, letterSpacing: '0.01em' },
        label: { padding: '0 10px' },
        colorSuccess: { backgroundColor: tokens.color.brandGreen100, color: '#1f4741' },
        colorWarning: { backgroundColor: tokens.color.tintWarning, color: tokens.color.warning },
        colorError: { backgroundColor: tokens.color.tintDanger, color: tokens.color.danger },
        colorInfo: { backgroundColor: tokens.color.brandTeal100, color: tokens.color.brandTeal },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: tokens.color.ink800, fontSize: 12, padding: '6px 10px', borderRadius: tokens.radius.sm },
        arrow: { color: tokens.color.ink800 },
      },
    },
    MuiDialog: { styleOverrides: { paper: { borderRadius: tokens.radius.lg, boxShadow: tokens.shadow.float } } },
    MuiDialogTitle: { styleOverrides: { root: { fontSize: 18, fontWeight: 600, padding: '20px 24px 8px' } } },
    MuiSwitch: {
      styleOverrides: {
        root: { width: 36, height: 20, padding: 0 },
        switchBase: {
          padding: 2,
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            '& + .MuiSwitch-track': { backgroundColor: tokens.color.brandDeep, opacity: 1 },
          },
        },
        thumb: { width: 16, height: 16, boxShadow: 'none' },
        track: { borderRadius: 999, backgroundColor: tokens.color.ink300, opacity: 1 },
      },
    },
    MuiCheckbox: { styleOverrides: { root: { padding: 6, color: tokens.color.ink400 } } },
    MuiDivider: { styleOverrides: { root: { borderColor: tokens.color.ink200 } } },
    MuiLink: {
      defaultProps: { underline: 'hover', color: tokens.color.brandTeal },
      styleOverrides: { root: { fontWeight: 500, cursor: 'pointer' } },
    },
    MuiAlert: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: { borderRadius: tokens.radius.md, padding: '10px 14px', alignItems: 'flex-start', fontSize: 13 },
        outlinedInfo: { borderColor: '#bbd6e0', backgroundColor: '#eef4f8', color: '#18576e' },
        outlinedSuccess: { borderColor: tokens.color.brandGreen300, backgroundColor: tokens.color.brandGreen100, color: '#1f4741' },
        outlinedWarning: { borderColor: '#f0d999', backgroundColor: '#fdf6e4', color: '#7d5a1a' },
        outlinedError: { borderColor: '#f0c2b4', backgroundColor: '#fbeae3', color: '#7a3020' },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            fontSize: 11, fontWeight: 500, color: tokens.color.ink400,
            letterSpacing: '0.08em', textTransform: 'uppercase',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { fontSize: 13, padding: '0 16px', height: 44, borderBottomColor: tokens.color.ink200 },
        head: { height: 36 },
        body: { fontVariantNumeric: 'tabular-nums' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 6, borderRadius: 999, backgroundColor: tokens.color.ink100 },
        bar: { backgroundColor: tokens.color.brandTeal },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40, borderBottom: `1px solid ${tokens.color.ink200}` },
        indicator: { backgroundColor: tokens.color.brandDeep, height: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40, padding: '10px 16px', fontSize: 13, fontWeight: 500,
          textTransform: 'none', color: tokens.color.ink500,
          '&.Mui-selected': { color: tokens.color.ink700 },
        },
      },
    },
  },
}

export const theme = createTheme(options)
